/*****************************************************************************************
 * 설명 : 관리자 > 시스템설정 > 권한그룹 관리
 * URI : /admin/site
*****************************************************************************************/
import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';

import { BtnSearch } from 'components/common';
import BtnAgGridSave from 'components/btnAgGridSave';
import ChipEx from 'components/chipEx';
import { ButtonEx } from 'components/inputEx';
import alertMsg from 'components/message';

import { Restful } from 'service/restful';
import { comma } from 'service/utils';

import { MESSAGE_DELAY } from 'config/config';
import useGridConfig from 'hooks/useGridConfig';

import Layout from 'pages/admin/layout/layout';
import AuthGroupModal from 'pages/admin/system/authGroupModal';

/*****************************************************************************************
 * 설명 : 함수 선언
*****************************************************************************************/
const AuthGroup = ( props ) => {

  /***************************************************************************************
   * 설명 : 변수 선언부
  ***************************************************************************************/
  const [gridApi, setGridApi] = useState({});
  const [getApi] = Restful();

  const currentMenu = useSelector(state => state?.menuList?.current);

  const [list, setList] = useState([]);

  const [openModal, setOpenModal] = useState({open: false, modal: 'add', data: []});

  // table column
  const [columnDefs, setColumnDefs] = useState([
    { headerName: '관리번호(관리자용)', field: 'seq', width: 30, cellClass: 'center', flex: 0,
      headerCheckboxSelection: true, headerCheckboxSelectionFilteredOnly: true, checkboxSelection: true },
    { headerName: '정렬', field: 'sortOrder', hide: true },
    { headerName: '정렬', field: '', width: 55, cellClass: 'cp center', rowDrag: true, filter: false, editable: false },
    { headerName: '번호', field: 'seq', width: 55, cellClass: 'cp right',
      valueGetter: (params) => {
        return comma(params.node.rowIndex + 1);
      }
    },
    { headerName:'권한그룹명', field: 'groupName', width: 300, cellClass: 'cp' },
    { headerName:'권한레벨', field: 'memberLevel', width: 100, cellClass: 'cp right' },
    { headerName: '사용여부', field: 'isUse', width: 100, cellClass: 'cp center',
      cellRenderer: function(params) {
        if( parseInt(params.data.isUse) === 1 )
          return <ChipEx color="success" variant="outlined" size="small" label="사용" />;
        else
          return <ChipEx color="default" variant="outlined" size="small" label="사용안함" />;
      }
    },
    {headerName: '수정', field: 'modify', cellClass: 'cp center', width: 100,
      cellRenderer: function (params) {
        return <ButtonEx title="수정" auth="authModify" color="secondary" variant="outlined" className="gridBtn" onClick={() => setOpenModal({open: true, modal: 'add', data: params.data})} />;
      }
    }
  ]);

  const [gridConfig, setGridUpdate] = useGridConfig(1, setColumnDefs);

  /***************************************************************************************
   * 설명 : html 선언부
  ***************************************************************************************/
  const getAuthGroup = () => {
    let params = {
      program: 'admin',
      service: 'system',
      action: 'getAuthGroup',
      version: '1.0'
    }

    getApi("get", params).then( response => {
      if( response !== undefined && response.data.result ) {
        setList(response.data.data);
      } else {
        setList([]);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 권한그룹 삭제
  ***************************************************************************************/
  const deleteAuthGroup = () => {
    let nodes = gridApi.getSelectedRows();

    if( nodes.length < 1 ) {
      alertMsg("삭제할 권한그룹을 선택하시기 바랍니다.", "W", MESSAGE_DELAY);
      return;
    }

    if( ! window.confirm("선택한 권한그룹을 삭제하시겠습니까?") ) return;

    let params = {
      program: 'admin',
      service: 'system',
      action: 'deleteAuthGroup',
      version: '1.0',
    }

    params.seq = nodes.map((item) => item.seq).join();

    getApi("post", params).then( response => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        getAuthGroup();
      } else {
        alertMsg(response?.data?.message || '서버와의 통신에 실패하였습니다.', 'E', MESSAGE_DELAY);
      }
    })
  }

  /***************************************************************************************
   * 설명 : 순서 저장
  ***************************************************************************************/
  const setSortAuthGroup = () => {
    let nodes = gridApi.getSelectedRows();

    if( nodes.length < 1 ) {
      if( ! window.confirm("권한그룹 전체의 순서를 저장하시겠습니까?") ) return;

      nodes = [];
      gridApi.forEachNode((item) => {
        nodes.push(item.data);
      });
    }

    if( ! window.confirm("선택한 권한그룹의 순서를 저장하시겠습니까?") ) return;

    let params = {
      program: 'admin',
      service: 'system',
      action: 'setSortAuthGroup',
      version: '1.0',
    }

    params.seq = nodes.map((item) => item.seq).join();

    getApi("post", params).then( response => {
      if( response !== undefined && response.data.result ) {
        alertMsg(response.data.message, "S", MESSAGE_DELAY);

        getAuthGroup();
      } else {
        alertMsg(response?.data?.message || '서버와의 통신에 실패하였습니다.', 'E', MESSAGE_DELAY);
      }
    });
  }

  /***************************************************************************************
   * 설명 : 데이터 처리
  ***************************************************************************************/
  useEffect(() => {
    getAuthGroup();

    // eslint-disable-next-line
  }, []);

  /***************************************************************************************
   * 설명 : html 선언부
  ***************************************************************************************/
  return (
    <Layout>
      <div className="admin-cms-contents">
        <header className="admin-cms-search-header">
          <div className="left">
            <BtnSearch click={() => getAuthGroup()}></BtnSearch>
            <BtnAgGridSave click={setGridUpdate}></BtnAgGridSave>
          </div>

          <div className="right">
            <ButtonEx
              title="순서저장"
              auth="authModify"
              color="inherit"
              variant="outlined"
              className="Btn"
              onClick={() => setSortAuthGroup()}
            />

            <ButtonEx
              title="권한그룹 삭제"
              auth="authDelete"
              color="error"
              variant="contained"
              className="ml5"
              onClick={() => deleteAuthGroup()}
            />

            <ButtonEx
              title="권한그룹 등록"
              auth="authWrite"
              color="primary"
              className="ml5"
              variant="contained"
              onClick={() => {
                setOpenModal({open: true, modal: 'add', data: {}})
              }}
            />
          </div>
        </header>

        <section className="admin-cms-body">
          <section className="ag-theme-balham grid" style={{height:'calc(100% - 50px)'}}>
            <div className="" style={{ height: '100%' }}>
              <AgGridReact
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                  filter: false,
                  lockVisible: true
                }}
                rowSelection = {'multiple'}
                columnDefs={columnDefs}
                rowData={list}
                onGridReady={(event) => {
                  gridConfig(event.columnApi);
                  setGridApi(event.api);
                }}
                onRowDoubleClicked={(event) => {
                  if( currentMenu?.authModify === '1' || currentMenu?.authModify === 1 )
                    setOpenModal({open: true, modal: 'add', data: event.data});
                }}
                rowDragManaged={true}
                rowDragMultiRow={true}
                animateRows={true}
                overlayNoRowsTemplate = "검색된 내용이 없습니다."
              />
            </div>
          </section>
        </section>
      </div>

      { openModal.open &&
        <AuthGroupModal
          open={openModal.open}
          close={() => setOpenModal({ open: false, modalName: 'add', data: openModal.data })}
          data={openModal.data}
          getList={getAuthGroup}
          indicator={props.indicator}
          history={props.history}
        />
      }
    </Layout>
  );
}

/*****************************************************************************************
 * 설명 : default export 선언
*****************************************************************************************/
export default AuthGroup;